/**
 * @file billQtyCheck.js
 * @version 1.4.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-05
 * @modified 2026-10-22
 * @description How far a bill line's quantity may stray from its PO line's
 *              before the user is told to query it with the supplier (Cam,
 *              5 Oct 2026).
 *
 * The rule:
 *   - Bulk lines (counted in tonnes): more than 0.5 T apart → query it.
 *   - Bagged lines (counted in bags, totes, tubs …): more than 1 bag apart →
 *     query it.
 *   - Anything smaller (a weighbridge's few kilos) → only the gentle note.
 *
 * What a line is counted in, first answer wins:
 *   0. A line set to None — no weight (unit weight 0 on the bill line or the
 *      PO line; utils/lineUnits, Cam 2026-10-12): a plain count of units —
 *      more than 1 apart → query it. Never the item's weight in its place.
 *   1. The item's unit weight (inventory.items.unit_weight_kg, or the line's
 *      own): 1000 kg is a tonne → bulk; any other weight (20/25 kg bags,
 *      500 kg totes) is one unit each → bagged.
 *   2. No weight, but the item's sales account is a bulk one (utils/units
 *      BULK_SALES_ACCOUNT_CODES) → bulk.
 *   3. The words on the line or the PO line: "bulk", "tonne(s)", a number with
 *      "T"/"TO" → bulk; a number with "kg", "bag(s)", "sack(s)", "tote(s)",
 *      "tub(s)", "bucket(s)" → bagged.
 *   4. A quantity with a fraction (8.01) → bulk (bags come whole).
 *   5. Otherwise unknown, judged as bulk — the tighter limit, so a doubt is
 *      raised rather than missed.
 *
 * 1.1.0 (2026-10-06, Cam): the bill is checked against what was SUPPLIED —
 * our driver's or weighbridge figure — on a PO-D line that has one recorded;
 * against the ordered quantity otherwise (always on a Xero-made PO, which
 * has no supplied quantity of its own). compareQuantity picks which; the
 * flag's words say which it was.
 *
 * 1.3.0 (2026-10-13, Jade — "Bill & PO Units Differ"): a bill priced per
 * tonne against a PO line counted in packs (or the other way round) is
 * compared in the PO line's unit (utils/billUnitConversion — the server's
 * rule too): 1.00 T billed against 40 × 25kg bag supplied is no difference.
 * billLineQuantityCheck does both; the flag's words show the conversion.
 *
 * 1.4.0 (2026-10-14, Cam — Lacto Production): billed per KG too, and on a PO
 * line with no cost by its quantity alone (utils/billUnitConversion 1.1.0);
 * the invoice's own unit (`billUnit`, else the line's `bill_unit`) is passed
 * on as evidence. The flag shows the PO side in packs whenever the PO line is
 * counted in packs (`poUnit`).
 */

import { weightKgOf, isBulkSalesCode, unitLabelFor } from './units';
import { isNoWeight } from './lineUnits';
import { formatQuantity, formatCount, formatTonnes } from './numberFormatters';
import { billUnitConversion, conversionQtyText } from './billUnitConversion';

export const QTY_LIMITS = Object.freeze({ tonnes: 0.5, bags: 1, units: 1 });
const EPS = 0.0001;

const BULK_WORDS = /\bbulk\b|\btonnes?\b|\b\d+(?:\.\d+)?\s*(?:t|to)\b/i;
const BAG_WORDS = /\b\d+(?:\.\d+)?\s*kgs?\b|\bbags?\b|\bsacks?\b|\btotes?\b|\btubs?\b|\bbuckets?\b/i;

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};
const fractional = (v) => {
  const n = num(v);
  return n !== null && Math.abs(n - Math.round(n)) > EPS;
};

/**
 * @param {object} p
 * @param {object|null} [p.item]   — the inventory item (unit_weight_kg, sales_account_code)
 * @param {object|null} [p.line]   — the bill line (unit_weight_kg, product_name, quantity)
 * @param {object|null} [p.poLine] — the PO line (unitWeightKg, description, quantity)
 * @returns {{ kind: 'tonnes'|'bags'|'units', basis: 'none'|'unit'|'account'|'words'|'fraction'|'assumed' }}
 */
export function quantityUnitKind({ item = null, line = null, poLine = null } = {}) {
  // None wins over the item's weight: the line says it carries none.
  if (isNoWeight(line?.unit_weight_kg) || isNoWeight(poLine?.unitWeightKg)) return { kind: 'units', basis: 'none' };
  const kg = weightKgOf(line?.unit_weight_kg) ?? weightKgOf(item?.unit_weight_kg) ?? weightKgOf(poLine?.unitWeightKg);
  if (kg !== null) return { kind: kg === 1000 ? 'tonnes' : 'bags', basis: 'unit' };
  if (isBulkSalesCode(item?.sales_account_code)) return { kind: 'tonnes', basis: 'account' };
  const words = `${line?.product_name || ''} ${poLine?.description || ''}`;
  if (BULK_WORDS.test(words)) return { kind: 'tonnes', basis: 'words' };
  if (BAG_WORDS.test(words)) return { kind: 'bags', basis: 'words' };
  if (fractional(line?.quantity) || fractional(poLine?.quantity)) return { kind: 'tonnes', basis: 'fraction' };
  return { kind: 'tonnes', basis: 'assumed' };
}

/**
 * @param {{ billQty: number|string, poQty: number|string, kind: 'tonnes'|'bags' }} p
 * @returns {{ level: 'none'|'note'|'query', diff: number, limit: number }}
 */
export function quantityCheck({ billQty, poQty, kind = 'tonnes' }) {
  const b = num(billQty);
  const p = num(poQty);
  const limit = QTY_LIMITS[kind] ?? QTY_LIMITS.tonnes;
  if (b === null || p === null) return { level: 'none', diff: 0, limit };
  const diff = Math.round(Math.abs(b - p) * 10000) / 10000;
  if (diff < EPS) return { level: 'none', diff: 0, limit };
  return { level: diff > limit + EPS ? 'query' : 'note', diff, limit };
}

// Tonnes in the flag follow the quantity rule (2dp, more when the figure has
// them); bags are a count — only the places they have (Cam, 2026-10-08).
const counted = (kind) => kind === 'bags' || kind === 'units';
const tidy = (n, kind) => (counted(kind) ? formatCount : formatQuantity)(Number(n) || 0);

/**
 * What a bill line's quantity is checked against: the supplied quantity
 * recorded for its PO line, else the PO line's (ordered) quantity.
 * @param {{ ordered: number|string, supplied?: number|string|null, xeroMade?: boolean }} p
 * @returns {{ qty: number, basis: 'supplied'|'ordered' }}
 */
export function compareQuantity({ ordered, supplied = null, xeroMade = false }) {
  const s = num(supplied);
  if (!xeroMade && s !== null) return { qty: s, basis: 'supplied' };
  return { qty: num(ordered) ?? 0, basis: 'ordered' };
}

/**
 * A bill's quantity for a PO line against what was supplied (else ordered),
 * in the PO line's unit — converted first when the bill is in another unit
 * (per tonne against packs, or packs against tonnes).
 *
 * @param {object} p
 * @param {object|null} [p.item]   — the bill line's item (for quantityUnitKind)
 * @param {object|null} [p.line]   — the bill line
 * @param {object|null} [p.poLine] — the PO line (unitWeightKg, itemCode, …)
 * @param {number|string|null} [p.itemWeight] — the PO line's item's unit_weight_kg
 * @param {number} p.billQty   — the bill's quantity for the PO line (its lines added up)
 * @param {number|null} [p.billCost] — the bill's unit price for it
 * @param {number|null} [p.billAmount] — what its lines come to, net
 * @param {string|null} [p.billUnit] — the invoice's unit for it (billUnitOf); absent → the line's `bill_unit`
 * @param {number|null} [p.poCost]   — the PO line's unit cost (as edited)
 * @param {number} p.poQty     — what it is compared with (compareQuantity)
 * @param {number|null} [p.ordered] — the PO line's ordered quantity
 * @returns {{ level, diff, limit, kind, billQty: number, conversion: object|null }}
 *   billQty — in the PO line's unit
 */
export function billLineQuantityCheck({ item = null, line = null, poLine = null, itemWeight = null, billQty, billCost = null, billAmount = null, billUnit, poCost = null, poQty, ordered = null }) {
  const conversion = isNoWeight(line?.unit_weight_kg) ? null : billUnitConversion({
    billQty, billCost, billAmount, poCost, poQty: [ordered, poQty],
    billUnit: billUnit === undefined ? (line?.bill_unit ?? null) : billUnit,
    lineWeight: poLine?.unitWeightKg, itemWeight: itemWeight ?? item?.unit_weight_kg ?? null,
    poWords: poLine?.description || null,
  });
  const kind = conversion ? conversion.kind : quantityUnitKind({ item, line, poLine }).kind;
  const qty = conversion ? conversion.quantity : num(billQty);
  return { ...quantityCheck({ billQty: qty, poQty, kind }), kind, billQty: qty, conversion };
}

/** The prominent flag's words for a line to query with the supplier. */
export function queryQuantityText({ billQty, poQty, kind, basis = 'ordered', conversion = null }) {
  const unit = counted(kind) ? '' : ' T';
  const limit = kind === 'bags' ? `${formatCount(QTY_LIMITS.bags)} bag`
    : kind === 'units' ? `${formatCount(QTY_LIMITS.units)}` : formatTonnes(QTY_LIMITS.tonnes);
  const what = basis === 'supplied' ? 'supplied' : 'PO';
  // Billed in another unit: the bill as invoiced, and what it comes to.
  const bill = conversion ? conversionQtyText(conversion) : `${tidy(billQty, kind)}${unit}`;
  const po = conversion && conversion.poUnit === 'packs'
    ? `${formatCount(poQty)} × ${unitLabelFor(conversion.kg)}`
    : `${tidy(poQty, kind)}${unit}`;
  return `Bill ${bill}, ${what} ${po} — more than ${limit} apart. Query this with the supplier.`;
}
